import { useState, useEffect, useRef, useCallback } from 'react';
import type { AccessPoint, FlowSegmentData, IntersectionTrafficData } from '../types/traffic';

const TOMTOM_KEY = import.meta.env.VITE_TOMTOM_API_KEY as string | undefined;
const POLL_INTERVAL = 120_000; // 120s

// ~150m fuera del cruce Aragó × Pg. de Gràcia en cada acceso
const ACCESS_POINTS: AccessPoint[] = [
  { id: 1, label: 'Aragó E',      lat: 41.39075, lon: 2.16680 },
  { id: 2, label: 'Aragó W',      lat: 41.39075, lon: 2.16340 },
  { id: 3, label: 'Pg. Gràcia N', lat: 41.39210, lon: 2.16510 },
  { id: 4, label: 'Pg. Gràcia S', lat: 41.38940, lon: 2.16510 },
];

async function fetchSegment(point: AccessPoint): Promise<FlowSegmentData> {
  const url = `https://api.tomtom.com/traffic/services/4/flowSegmentData/relative0/18/json?key=${TOMTOM_KEY}&point=${point.lat},${point.lon}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`TomTom ${res.status}`);
  const json = await res.json();
  const d = json.flowSegmentData;
  return {
    accessId: point.id,
    label: point.label,
    currentSpeed: d.currentSpeed,
    freeFlowSpeed: d.freeFlowSpeed,
    congestionRatio: d.freeFlowSpeed > 0 ? d.currentSpeed / d.freeFlowSpeed : 0,
    roadClosure: d.roadClosure ?? false,
    confidence: d.confidence ?? 1,
  };
}

export function useTrafficFlow(): IntersectionTrafficData {
  const [data, setData] = useState<IntersectionTrafficData>({
    segments: [],
    lastUpdate: null,
    isLoading: false,
    error: null,
  });
  const timerRef = useRef<ReturnType<typeof setInterval>>();
  const visibleRef = useRef(true);

  const poll = useCallback(async () => {
    if (!TOMTOM_KEY || !visibleRef.current) return;
    setData(prev => ({ ...prev, isLoading: true, error: null }));
    try {
      const segments = await Promise.all(ACCESS_POINTS.map(fetchSegment));
      setData({ segments, lastUpdate: new Date(), isLoading: false, error: null });
    } catch (e) {
      setData(prev => ({ ...prev, isLoading: false, error: (e as Error).message }));
    }
  }, []);

  useEffect(() => {
    if (!TOMTOM_KEY) return;

    poll();
    timerRef.current = setInterval(poll, POLL_INTERVAL);

    const onVisibility = () => {
      visibleRef.current = document.visibilityState === 'visible';
      if (visibleRef.current) {
        poll();
        clearInterval(timerRef.current);
        timerRef.current = setInterval(poll, POLL_INTERVAL);
      } else {
        clearInterval(timerRef.current);
      }
    };

    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      clearInterval(timerRef.current);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [poll]);

  return data;
}
